import React, { useState, CSSProperties, ChangeEvent, FocusEvent, useRef } from 'react';
import { useAppSelector } from '../../store/hooks';
import CText from './CText';
import { FiEye, FiEyeOff } from 'react-icons/fi';

interface CInputProps {
  value?: string;
  label?: string;
  bgColor?: string;
  inputContainerStyle?: CSSProperties;
  inputBoxStyle?: CSSProperties;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  errorText?: string;
  autoFocus?: boolean;
  maxLength?: number;
  editable?: boolean;
  autoCapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters';
  required?: boolean;
  labelStyle?: CSSProperties;
  multiline?: boolean;
  errorStyle?: CSSProperties;
  isSecure?: boolean;
  textColor?: string;
  labelTextColor?: string;
  rightAccessory?: () => React.ReactNode;
  typeText?: string;
  insideLeftIcon?: () => React.ReactNode;
  showError?: boolean;
  className?: string;
  inputClassName?: string;
}

const CInput: React.FC<CInputProps> = ({
  value = '',
  label,
  bgColor,
  inputContainerStyle,
  inputBoxStyle,
  onChange,
  placeholder,
  type = 'text',
  onFocus,
  onBlur,
  errorText,
  autoFocus = false,
  maxLength,
  editable = true,
  autoCapitalize,
  required = false,
  labelStyle,
  multiline = false,
  errorStyle,
  isSecure = false,
  textColor,
  labelTextColor,
  rightAccessory,
  typeText,
  insideLeftIcon,
  showError = true,
  className = '',
  inputClassName = '',
  ...props
}) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [isSecureVisible, setIsSecureVisible] = useState(!isSecure);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const toggleSecureVisibility = () => {
    setIsSecureVisible(!isSecureVisible);
  };

  const handleFocusInternal = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlurInternal = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const InputElement = multiline ? 'textarea' : 'input';

  // Get container styles based on state
  const getContainerStyles = (): CSSProperties => {
    const baseStyles: CSSProperties = {
      backgroundColor: bgColor || colors.inputBackground,
      transition: 'all 0.2s ease',
      boxShadow: colors.shadowSoft,
    };

    if (errorText) {
      return {
        ...baseStyles,
        borderColor: colors.alertColor,
        boxShadow: `0 0 0 3px ${colors.alertColor}20`,
      };
    }

    if (isFocused) {
      return {
        ...baseStyles,
        borderColor: colors.primary,
        boxShadow: `0 0 0 3px ${colors.primary}20, ${colors.shadowGlow}`,
      };
    }

    return {
      ...baseStyles,
      borderColor: colors.borderColor,
    };
  };

  return (
    <div className={`mb-3 w-full lg:max-w-lg ${className}`}>
      {label && (
        <div
          className="flex justify-between items-center mb-2"
          style={labelStyle}
        >
          <div className="flex flex-row">
            <CText
              style={{ textAlign: 'left', fontWeight: 500 }}
              color={labelTextColor || colors.grayScale500}
              type={typeText || 'r14'}
            >
              {label}
            </CText>
            {required && (
              <CText color={colors.alertColor} style={{ marginLeft: 2 }}>*</CText>
            )}
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={`
          flex flex-row items-center justify-between w-full rounded-xl border
          ${multiline ? 'min-h-24' : 'h-12 md:h-14'}
        `}
        style={{
          ...getContainerStyles(),
          ...inputContainerStyle,
        }}
      >
        {insideLeftIcon && (
          <div className="pl-3">{insideLeftIcon()}</div>
        )}

        <InputElement
          value={value}
          maxLength={maxLength}
          autoFocus={autoFocus}
          autoCorrect="off"
          autoCapitalize={autoCapitalize}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocusInternal}
          onBlur={handleBlurInternal}
          disabled={!editable}
          type={isSecure && !isSecureVisible ? 'password' : type}
          className={`
            flex-1 p-3 bg-transparent border-none rounded-xl
            text-base font-sans outline-none w-[90%]
            placeholder:text-gray-400
            ${multiline ? 'min-h-24 resize-y' : 'h-12 md:h-14 resize-none'}
            ${!editable ? 'cursor-not-allowed opacity-60' : ''}
            ${inputClassName}
          `}
          style={{
            color: textColor || colors.textColor,
            ...inputBoxStyle,
          }}
          {...(props as any)}
        />

        {rightAccessory && (
          <div className="mr-4">{rightAccessory()}</div>
        )}

        {isSecure && (
          <button
            type="button"
            onClick={toggleSecureVisibility}
            className="bg-transparent border-none cursor-pointer mr-3 flex items-center"
            style={{ color: colors.grayScale500 }}
          >
            {React.createElement(isSecureVisible ? FiEye as any : FiEyeOff as any, { size: 24 })}
          </button>
        )}
      </div>

      {errorText && errorText !== '' && (
        <p
          className="text-xs mt-1 ml-1"
          style={{ color: colors.alertColor, ...errorStyle }}
        >
          {errorText}
        </p>
      )}

      {maxLength && showError && value.length > maxLength && (
        <p
          className="text-xs mt-1 ml-1"
          style={{ color: colors.alertColor, ...errorStyle }}
        >
          It should be maximum {maxLength} characters
        </p>
      )}
    </div>
  );
};

export default CInput;
